import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Mic2, Headphones, Check, Clock } from 'lucide-react';
import { useApp } from '../context/AppContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ServicesPage = () => {
  const { addToCart } = useApp();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (service) => {
    addToCart({
      item_id: service.service_id,
      item_type: 'service',
      title: service.name,
      price: service.price
    });
  };

  // Separate courses from mixing services
  const courses = services.filter(s => s.category === 'course' || s.service_id?.includes('course'));
  const mixingServices = services.filter(s => s.category !== 'course' && !s.service_id?.includes('course'));

  return (
    <div className="min-h-screen pt-24 pb-32 px-6 lg:px-12" data-testid="services-page">
      <div className="max-w-7xl mx-auto">
        {/* Hero */}
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ fontFamily: 'Syne' }}>
            <span className="text-white">Services & </span>
            <span className="gradient-text">Courses</span>
          </h1>
          <p className="text-neutral-500 text-lg max-w-2xl mx-auto">
            Professional audio engineering and learn the sauce with our mixing courses. 
            Level up your sound and your skills.
          </p>
        </div>

        {/* Mixing Services Section - FIRST */}
        <section className="mb-20" data-testid="mixing-services-section">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#B8941F] flex items-center justify-center">
              <Mic2 size={24} className="text-black" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white" style={{ fontFamily: 'Syne' }}>Mix & Master Services</h2>
              <p className="text-neutral-500">Professional engineering for your tracks</p>
            </div>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="glass-card p-6 animate-pulse">
                  <div className="h-6 bg-neutral-800 rounded mb-4 w-3/4" />
                  <div className="h-4 bg-neutral-800 rounded mb-6 w-full" />
                  <div className="space-y-2">
                    {[...Array(4)].map((_, j) => (
                      <div key={j} className="h-3 bg-neutral-800 rounded w-full" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {mixingServices.map((service, index) => (
                <div 
                  key={service.service_id} 
                  className={`glass-card p-6 flex flex-col ${index === 3 ? 'ring-2 ring-[#D4AF37]' : ''}`}
                  data-testid={`service-${service.service_id}`}
                >
                  {index === 3 && (
                    <div className="text-xs font-bold text-[#D4AF37] uppercase tracking-wider mb-4">Best Value</div>
                  )}
                  <h3 className="text-xl font-bold mb-2 text-white" style={{ fontFamily: 'Syne' }}>{service.name}</h3>
                  <p className="text-neutral-500 text-sm mb-4">{service.description}</p>
                  
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="text-3xl font-bold gradient-text">${service.price}</span>
                    <span className="text-neutral-500 text-sm">CAD</span>
                  </div>

                  {service.turnaround_days > 0 && (
                    <div className="flex items-center gap-2 text-sm text-neutral-400 mb-6">
                      <Clock size={14} className="text-[#D4AF37]" />
                      <span>{service.turnaround_days} day turnaround</span>
                    </div>
                  )}

                  <ul className="space-y-3 mb-8 flex-1">
                    {service.features?.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-neutral-300">
                        <Check size={14} className="text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handleAddToCart(service)}
                    className={index === 3 ? 'btn-primary w-full' : 'btn-secondary w-full'}
                    data-testid={`add-service-${service.service_id}`}
                  >
                    Add to Cart
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Mix Course Section - SECOND WITH PROMO BANNER */}
        <section className="mb-16" data-testid="courses-section">
          {/* Course Promo Banner */}
          <div className="glass-card p-8 mb-8 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#D4AF37]/20 to-[#FFD700]/10 rounded-full blur-3xl -z-0" />
            <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-6">
              <div>
                <div className="inline-block px-3 py-1 bg-[#D4AF37]/20 text-[#D4AF37] text-xs font-bold rounded-full mb-3 border border-[#D4AF37]/30">
                  NEW: LEARN THE SAUCE
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-white" style={{ fontFamily: 'Syne' }}>
                  Mix Course - Level Up Your Skills
                </h2>
                <p className="text-neutral-400 max-w-xl">
                  Get lifetime access to all my mixing techniques, templates, and live coaching sessions. 
                  From beginner fundamentals to pro-level sauce.
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className="text-3xl font-bold gradient-text">$75+</p>
                  <p className="text-xs text-neutral-500">CAD Starting price</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#FFD700] flex items-center justify-center">
              <Headphones size={24} className="text-black" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white" style={{ fontFamily: 'Syne' }}>Mix Course Tiers</h2>
              <p className="text-neutral-500">Choose your level - lifetime access to video content</p>
            </div>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="glass-card p-6 animate-pulse">
                  <div className="h-6 bg-neutral-800 rounded mb-4 w-3/4" />
                  <div className="h-4 bg-neutral-800 rounded mb-6 w-full" />
                  <div className="space-y-2">
                    {[...Array(6)].map((_, j) => (
                      <div key={j} className="h-3 bg-neutral-800 rounded w-full" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-6">
              {courses.map((course, index) => (
                <div 
                  key={course.service_id} 
                  className={`glass-card p-6 flex flex-col relative overflow-hidden ${index === 2 ? 'ring-2 ring-[#FFD700]' : ''}`}
                  data-testid={`course-${course.service_id}`}
                >
                  {index === 2 && (
                    <div className="absolute top-0 right-0 bg-[#FFD700] text-black text-xs font-bold px-3 py-1 rounded-bl-lg">
                      FULL SAUCE
                    </div>
                  )}
                  {index === 1 && (
                    <div className="absolute top-0 right-0 bg-[#D4AF37] text-black text-xs font-bold px-3 py-1 rounded-bl-lg">
                      POPULAR
                    </div>
                  )}
                  
                  <h3 className="text-xl font-bold mb-2 text-white" style={{ fontFamily: 'Syne' }}>{course.name}</h3>
                  <p className="text-neutral-500 text-sm mb-4">{course.description}</p>
                  
                  <div className="flex items-baseline gap-2 mb-6">
                    <span className="text-4xl font-bold gradient-text">${course.price}</span>
                    <span className="text-neutral-500 text-sm">CAD one-time</span>
                  </div>

                  <ul className="space-y-3 mb-8 flex-1">
                    {course.features?.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-neutral-300">
                        <Check size={14} className="text-[#FFD700] mt-0.5 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handleAddToCart(course)}
                    className={index === 2 ? 'btn-primary w-full' : 'btn-secondary w-full'}
                    data-testid={`add-course-${course.service_id}`}
                  >
                    Get Access
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* E-transfer Info */}
        <div className="glass-card p-8">
          <h2 className="text-2xl font-bold mb-4 text-white" style={{ fontFamily: 'Syne' }}>E-Transfer Option</h2>
          <p className="text-neutral-400 mb-4">
            Prefer to pay via e-transfer? No problem! During checkout, select the e-transfer option 
            and you'll receive instructions to complete your payment to <span className="text-[#D4AF37] font-bold">gunandrive222@gmail.com</span>.
          </p>
          <div className="flex items-center gap-2 text-[#D4AF37]">
            <Check size={18} />
            <span>No additional fees • All prices in CAD</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicesPage;
